import { useMemo, useState } from 'react';
import { AlertCircle, PackageSearch } from 'lucide-react';
import type { ProcurementOrderRow } from '@contracts/procurement';
import { useProcurementOrders, useProcurementSummary } from '@/features/procurement/hooks/useProcurement';
import { useProcurementFilters } from '@/features/procurement/procurementContext';
import { BOARD_SECTIONS } from './boardSections';
import { ProcurementKpis } from './ProcurementKpis';
import { PurchaseTable } from './PurchaseTable';
import { PurchaseDrilldownModal } from '../drilldown/PurchaseDrilldownModal';

/** Tablero: KPIs + una sección por estado (config en boardSections). Los
 *  filtros viven en la ProcurementFilterBar (arriba, a lo ancho). */
export function ProcurementBoard() {
  const { period } = useProcurementFilters();
  const summaryQ = useProcurementSummary();
  const ordersQ = useProcurementOrders();
  const [selected, setSelected] = useState<ProcurementOrderRow | null>(null);

  const orders = ordersQ.data?.data ?? [];
  const byState = useMemo(() => {
    const groups = new Map<string, ProcurementOrderRow[]>();
    for (const o of orders) {
      const bucket = groups.get(o.state) ?? [];
      bucket.push(o);
      groups.set(o.state, bucket);
    }
    return groups;
  }, [orders]);

  const error = summaryQ.isError || ordersQ.isError;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-2">
        <PackageSearch className="h-5 w-5 text-red-700" />
        <h2 className="text-lg font-black tracking-tight text-slate-800">Compras y Gastos</h2>
      </div>

      {error ? (
        <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <span>
            No se pudieron cargar los datos de compras. Verifica que BridgeHub exponga{' '}
            <code className="rounded bg-red-100 px-1">/procurement</code> y vuelve a intentar.
          </span>
        </div>
      ) : (
        <>
          <ProcurementKpis summary={summaryQ.data?.data} loading={summaryQ.isLoading} />
          <div className="flex flex-col gap-4">
            {BOARD_SECTIONS.map((section) => (
              <PurchaseTable
                key={section.state}
                title={section.title}
                rows={byState.get(section.state) ?? []}
                loading={ordersQ.isLoading}
                onRowClick={setSelected}
                columnKeys={section.columns}
                accentDot={section.accentDot}
                collapsible
                defaultOpen={section.defaultOpen}
                emptyText={
                  period != null
                    ? 'Sin órdenes CREADAS en el periodo seleccionado — prueba "Histórico" para ver todo lo abierto.'
                    : section.emptyText
                }
              />
            ))}
          </div>
        </>
      )}

      <PurchaseDrilldownModal order={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
